import { apiDelete, apiGet, apiPatch, apiPost } from "../../client";
import { ApiConfig } from "../../config";
import type {
  Appointment,
  AppointmentListResponse,
  AppointmentMutationResponse,
  AvailableSlotsResponse,
  NextAppointmentResponse,
  UnavailableDatesResponse,
} from "./appointment.types";

const APPOINTMENT_ROOT = `${ApiConfig.CLINIC_BASE}/appointments/`;
const APPOINTMENT_NEXT = `${APPOINTMENT_ROOT}next/`;

type QueryValue = string | number | null | undefined;

export interface AppointmentListQuery {
  date?: QueryValue;
  start_date?: QueryValue;
  end_date?: QueryValue;
  patient_id?: QueryValue;
  patientId?: QueryValue;
  page?: QueryValue;
  page_size?: QueryValue;
  pageSize?: QueryValue;
  clinic_branch_id?: QueryValue;
}

export type AppointmentId = string | number;
export type AppointmentPayload = Record<string, unknown>;

function buildSearchParams(
  entries: Record<string, QueryValue>,
): URLSearchParams {
  const params = new URLSearchParams();

  Object.entries(entries).forEach(([key, value]) => {
    if (value !== undefined && value !== "" && value !== null) {
      params.append(key, String(value));
    }
  });

  return params;
}

function parseAppointmentId(appointmentId: AppointmentId): number {
  const rawId = String(appointmentId).trim();
  if (!/^\d+$/.test(rawId)) {
    throw new Error("รหัสนัดหมายไม่ถูกต้อง");
  }
  const numericId = Number(rawId);
  if (!Number.isSafeInteger(numericId) || numericId <= 0) {
    throw new Error("รหัสนัดหมายไม่ถูกต้อง");
  }
  return numericId;
}

/**
 * Get all appointments.
 *
 * GET /clinic/api/appointments/
 *
 * Query Parameters:
 * - date (optional): Filter appointments by date (YYYY-MM-DD).
 * - start_date (optional): Filter appointments from this date (YYYY-MM-DD).
 * - end_date (optional): Filter appointments up to this date (YYYY-MM-DD).
 * - patient_id (optional): Filter appointments by patient ID.
 * - page (optional): Page number for pagination.
 * - page_size (optional): Page size for pagination.
 * - clinic_branch_id (optional): Filter appointments by branch.
 */
export async function fetchAppointmentList(
  query: AppointmentListQuery = {},
): Promise<AppointmentListResponse> {
  const page = query.page ?? "";
  const pageSize = query.page_size ?? query.pageSize ?? "";
  const params = buildSearchParams({
    date: query.date,
    start_date: query.start_date ?? "",
    end_date: query.end_date ?? "",
    patient_id: query.patient_id ?? query.patientId ?? "",
    page: page,
    page_size: page ? pageSize : "",
    clinic_branch_id: query.clinic_branch_id ?? "",
  });

  const queryString = params.toString();
  return apiGet<AppointmentListResponse>(
    queryString ? `${APPOINTMENT_ROOT}?${queryString}` : APPOINTMENT_ROOT,
  );
}

/** Fetch a specific appointment by ID.
 *
 * GET /clinic/api/appointments/int:treatment_appointment_id/
 */
export async function fetchAppointment(
  appointmentId: AppointmentId,
): Promise<Appointment> {
  const numericId = parseAppointmentId(appointmentId);
  return apiGet<Appointment>(`${APPOINTMENT_ROOT}${numericId}/`);
}

/**
 * Create a new appointment.
 *
 * POST /clinic/api/appointments/
 */
export async function createAppointment(
  payload: AppointmentPayload,
): Promise<AppointmentMutationResponse> {
  return apiPost<AppointmentMutationResponse>(`${APPOINTMENT_ROOT}`, payload);
}

/**
 * Update an existing appointment.
 *
 * PATCH /clinic/api/appointments/int:treatment_appointment_id/
 */
export async function updateAppointment(
  appointmentId: AppointmentId,
  payload: AppointmentPayload,
): Promise<AppointmentMutationResponse> {
  const numericId = parseAppointmentId(appointmentId);
  return apiPatch<AppointmentMutationResponse>(
    `${APPOINTMENT_ROOT}${numericId}/`,
    payload,
  );
}

/**
 * Cancel an appointment.
 *
 * DELETE /clinic/api/appointments/int:treatment_appointment_id/
 */
export async function cancelAppointment(
  appointmentId: AppointmentId,
): Promise<AppointmentMutationResponse> {
  const numericId = parseAppointmentId(appointmentId);
  return apiDelete<AppointmentMutationResponse>(
    `${APPOINTMENT_ROOT}${numericId}/`,
  );
}

/**
 * Fetch available slots for a doctor on a specific date.
 *
 * GET /clinic/api/doctor/:doctor_id/available-slots/?date=YYYY-MM-DD
 *
 * Parameters:
 * - doctorId (number | string): Doctor ID (must be a finite integer).
 * - date (string): Date in "YYYY-MM-DD" format.
 * - patientId (number | string): Patient ID (must be a finite integer).
 * - clinicBranchId (number | string): Clinic branch ID (must be a finite integer).
 */
export async function fetchDoctorAvailableSlots(
  patientId: string | number,
  doctorId: string | number,
  date: string,
  clinicBranchId: string | number,
): Promise<AvailableSlotsResponse> {
  const params = buildSearchParams({
    date: date,
    doctor_id: doctorId,
    patient_id: patientId,
    clinic_branch_id: clinicBranchId,
  });

  const queryString = params.toString();
  return apiGet<AvailableSlotsResponse>(
    `${ApiConfig.CLINIC_BASE}/available-slots/?${queryString}`,
  );
}

/**
 * Fetch the list of unavailable dates for a doctor in a specific month.
 *
 * GET /clinic/api/doctors/unavailable-dates/
 *
 * Query Parameters:
 * - month (required): Month in "YYYY-MM" format.
 * - doctor_id (required): Doctor ID.
 * - patient_id (required): Patient ID.
 * - clinic_branch_id (required): Clinic branch ID.
 */
export async function getDoctorUnavailableDate(
  month: string,
  doctorId: string | number,
  patientId: string | number,
  clinicBranchId: string | number,
): Promise<UnavailableDatesResponse> {
  const params = buildSearchParams({
    month: month,
    doctor_id: doctorId,
    patient_id: patientId,
    clinic_branch_id: clinicBranchId,
  });

  const queryString = params.toString();
  return apiGet<UnavailableDatesResponse>(
    `${ApiConfig.CLINIC_BASE}/unavailable-dates/?${queryString}`,
  );
}

/**
 * Fetch the next upcoming appointment for a patient.
 *
 * GET /clinic/api/appointments/next/?patient_id=:patient_id
 */
export async function fetchNextAppointment(
  patientId: string | number,
): Promise<NextAppointmentResponse> {
  const params = buildSearchParams({ patient_id: patientId });
  return apiGet<NextAppointmentResponse>(
    `${APPOINTMENT_NEXT}?${params.toString()}`,
  );
}
